import { useState } from "react";

// ─── helpers ────────────────────────────────────────────────
function md(t) {
  if (!t) return "";
  return t
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(
      /`(.*?)`/g,
      `<code style="font-family:var(--font-mono);background:var(--cream-dark);padding:1px 5px;border-radius:4px;font-size:.85em">$1</code>`,
    )
    .replace(/\n/g, "<br/>");
}
function hi(code) {
  return code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/(@?"[^"]*")/g, '<span style="color:#E3B341">$1</span>')
    .replace(
      /\b(using|class|public|private|void|string|double|int|bool|var|new|return|if|else|foreach|for|in|true|false|null|try|catch|throw|static|switch|case|break|default|while)\b/g,
      '<span style="color:#58A6FF">$1</span>',
    )
    .replace(
      /\b(\d+\.?\d*)\b(?![A-Za-z])/g,
      '<span style="color:#79C0FF">$1</span>',
    )
    .replace(/([A-Z][a-zA-Z0-9]*)\(/g, '<span style="color:#D2A8FF">$1</span>(')
    .replace(
      /(\/\/[^\n]*)/g,
      '<span style="color:#6E7681;font-style:italic">$1</span>',
    );
}

// ─── block components ───────────────────────────────────────
function HintBlock({ block }) {
  const [open, setOpen] = useState(false);
  const content = block.data
    ? (block.data.content ?? block.data.text)
    : block.content;

  return (
    <IWrap>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 16px",
          background: open ? "#fffbeb" : "#faf8f5",
          border: "none",
          borderBottom: open ? "1px solid #fde68a" : "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background .2s",
        }}
      >
        <span style={{ fontSize: 18 }}>💡</span>
        <span
          style={{ fontSize: 13, fontWeight: 600, color: "#92400E", flex: 1 }}
        >
          How to approach this
        </span>
        <span style={{ fontSize: 12, color: "#b8975a" }}>
          {open ? "Hide ▲" : "Show hint ▼"}
        </span>
      </button>

      {open && (
        <div
          style={{
            padding: "14px 16px",
            background: "#fffbeb",
            fontSize: 13,
            color: "#78350F",
            lineHeight: 1.75,
          }}
          dangerouslySetInnerHTML={{ __html: md(content) }}
        />
      )}
    </IWrap>
  );
}
function Txt({ c }) {
  return (
    <p
      style={{
        fontSize: "1rem",
        lineHeight: 1.8,
        color: "var(--ink-soft)",
        marginBottom: 16,
      }}
      dangerouslySetInnerHTML={{ __html: md(c) }}
    />
  );
}

function Code({ code, label }) {
  const [cp, setCp] = useState(false);
  return (
    <div
      style={{
        background: "#0D1117",
        borderRadius: "var(--r-md)",
        overflow: "hidden",
        margin: "20px 0",
      }}
    >
      <div
        style={{
          background: "#161B22",
          padding: "7px 14px",
          display: "flex",
          alignItems: "center",
          gap: 7,
          borderBottom: "1px solid #30363D",
        }}
      >
        {["#FF5F57", "#FEBC2E", "#28C840"].map((c) => (
          <div
            key={c}
            style={{ width: 9, height: 9, borderRadius: "50%", background: c }}
          />
        ))}
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "#6E7681",
            marginLeft: 6,
            flex: 1,
          }}
        >
          {label || "C#"}
        </span>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(code);
            setCp(true);
            setTimeout(() => setCp(false), 1500);
          }}
          style={{
            fontSize: 10,
            color: "#6E7681",
            background: "none",
            border: "0.5px solid #30363D",
            borderRadius: 4,
            padding: "2px 7px",
            cursor: "pointer",
          }}
        >
          {cp ? "copied!" : "copy"}
        </button>
      </div>
      <pre
        style={{
          padding: "16px 18px",
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          lineHeight: 1.9,
          color: "#E6EDF3",
          overflowX: "auto",
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
        dangerouslySetInnerHTML={{ __html: hi(code) }}
      />
    </div>
  );
}

function Callout({ c, type = "amber" }) {
  const S = {
    amber: { b: "#D97706", bg: "#FFFBEB", col: "#78350F" },
    blue: { b: "#2563EB", bg: "#EFF6FF", col: "#1e3a8a" },
    green: { b: "#059669", bg: "#ECFDF5", col: "#065F46" },
  };
  const s = S[type] || S.amber;
  return (
    <div
      style={{
        borderLeft: `3px solid ${s.b}`,
        background: s.bg,
        borderRadius: "0 var(--r-md) var(--r-md) 0",
        padding: "13px 16px",
        fontSize: "0.9rem",
        color: s.col,
        margin: "16px 0",
        lineHeight: 1.7,
      }}
      dangerouslySetInnerHTML={{ __html: md(c) }}
    />
  );
}
// eslint-disable-next-line no-unused-vars
function LogicChips({ items }) {
  const T = {
    Logic: { bg: "#EDE9FE", col: "#6D28D9" },
    Condition: { bg: "#D1FAE5", col: "#065F46" },
    Rule: { bg: "#FEF3C7", col: "#92400E" },
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        margin: "16px 0",
      }}
    >
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "11px 14px",
            background: "#fff",
            border: "1px solid var(--cream-dark)",
            borderRadius: "var(--r-md)",
          }}
        >
          <span style={{ fontSize: "0.9rem", color: "var(--ink-soft)" }}>
            {item.icon && <span style={{ marginRight: 8 }}>{item.icon}</span>}
            {item.text}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: 99,
              background: T[item.tag]?.bg,
              color: T[item.tag]?.col,
            }}
          >
            {item.tag}
          </span>
        </div>
      ))}
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
function Comparison({ left, right }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        margin: "16px 0",
      }}
    >
      {[left, right].map((s, si) => (
        <div
          key={si}
          style={{
            padding: 16,
            borderRadius: "var(--r-md)",
            background: si === 0 ? "var(--cream-dark)" : "var(--blue-light)",
            border: `1px solid ${si === 0 ? "var(--ink-faint)" : "var(--blue-mid)"}`,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: ".08em",
              textTransform: "uppercase",
              color: si === 0 ? "var(--ink-muted)" : "var(--blue)",
              marginBottom: 10,
            }}
          >
            {s.label}
          </div>
          {s.items.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 7,
                fontSize: "0.85rem",
                color: si === 0 ? "var(--ink-muted)" : "var(--blue)",
                padding: "3px 0",
              }}
            >
              <span>{si === 0 ? "✓" : "→"}</span>
              {item}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
function TypesGrid({ types }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3,1fr)",
        gap: 8,
        margin: "16px 0",
      }}
    >
      {types.map((t, i) => (
        <div
          key={i}
          style={{
            background: "var(--cream)",
            border: "1px solid var(--cream-dark)",
            borderRadius: "var(--r-md)",
            padding: "10px 12px",
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--blue)",
              fontWeight: 600,
              marginBottom: 3,
            }}
          >
            {t.name}
          </div>
          <div
            style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}
          >
            {t.desc}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--amber)",
              marginTop: 3,
            }}
          >
            {t.example}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── interactive blocks ─────────────────────────────────────
function IHead({ icon, title, tag, tagColor }) {
  const TC = {
    quiz: "#DBEAFE:#1E40AF",
    debug: "#FEF3C7:#92400E",
    complete: "#D1FAE5:#065F46",
    assemble: "#EDE9FE:#6D28D9",
    read: "#D1FAE5:#065F46",
  };
  const [bg, col] = (TC[tagColor] || TC.quiz).split(":");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 16px",
        background: "var(--cream)",
        borderBottom: "1px solid var(--cream-dark)",
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
        {title}
      </span>
      <span
        style={{
          marginLeft: "auto",
          fontSize: 10,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 99,
          background: bg,
          color: col,
        }}
      >
        {tag}
      </span>
    </div>
  );
}

function IWrap({ children }) {
  return (
    <div
      style={{
        border: "1px solid var(--cream-dark)",
        borderRadius: "var(--r-lg)",
        overflow: "hidden",
        margin: "20px 0",
      }}
    >
      {children}
    </div>
  );
}

function Quiz({
  question,
  options,
  answer,
  correct,
  feedback,
  explanation,
  onCorrect,
}) {
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  const correctIndex = answer ?? correct;
  function pick(i) {
    if (done) return;
    setSel(i);
    setDone(true);
    if (i === correctIndex) onCorrect?.();
  }
  return (
    <IWrap>
      <IHead icon="⚡" title="Quick check" tag="Quiz" tagColor="quiz" />
      <div style={{ padding: 16 }}>
        <p
          style={{
            fontWeight: 600,
            color: "var(--ink)",
            marginBottom: 14,
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          {question}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {options.map((opt, i) => {
            let bg = "#fff",
              border = "var(--cream-dark)",
              col = "var(--ink-soft)";
            if (done && i === correctIndex) {
              bg = "#ECFDF5";
              border = "#059669";
              col = "#065F46";
            } else if (done && i === sel && i !== correctIndex) {
              bg = "#FEF2F2";
              border = "#DC2626";
              col = "#991B1B";
            } else if (!done && i === sel) {
              bg = "var(--blue-light)";
              border = "var(--blue)";
              col = "var(--blue)";
            }
            return (
              <div
                key={i}
                onClick={() => pick(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 13px",
                  border: `1.5px solid ${border}`,
                  borderRadius: "var(--r-md)",
                  cursor: done ? "default" : "pointer",
                  background: bg,
                  color: col,
                  fontSize: "0.9rem",
                  transition: "all .12s",
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1.5px solid ${border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                    background:
                      done && i === correctIndex
                        ? "#059669"
                        : done && i === sel
                          ? "#DC2626"
                          : "transparent",
                    color:
                      done && (i === correctIndex || i === sel)
                        ? "#fff"
                        : "inherit",
                  }}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                {opt}
              </div>
            );
          })}
        </div>
        {done && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 13px",
              borderRadius: "var(--r-md)",
              background: sel === correctIndex ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${sel === correctIndex ? "#6EE7B7" : "#FCA5A5"}`,
              fontSize: "0.875rem",
              color: sel === correctIndex ? "#065F46" : "#991B1B",
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{
              __html:
                (sel === (correctIndex ?? correct) ? "✓ " : "✗ ") +
                md(explanation || feedback),
            }}
          />
        )}
      </div>
    </IWrap>
  );
}

function SpotBug({ lines, bugLine, explanation, onCorrect }) {
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  function pick(i) {
    if (done) return;
    setSel(i);
    setDone(true);
    if (i === bugLine) onCorrect?.();
  }
  return (
    <IWrap>
      <IHead icon="🔍" title="Spot the bug" tag="Debug" tagColor="debug" />
      <div style={{ padding: 16 }}>
        <p
          style={{
            fontSize: "0.875rem",
            color: "var(--ink-muted)",
            marginBottom: 12,
          }}
        >
          One line has a bug. Click the broken line.
        </p>
        <div
          style={{
            background: "#0D1117",
            borderRadius: "var(--r-md)",
            overflow: "hidden",
          }}
        >
          {lines.map((line, i) => {
            let bg = "transparent";
            if (done && i === bugLine) bg = "rgba(220,38,38,.15)";
            else if (done && i !== bugLine) bg = "rgba(5,150,105,.08)";
            return (
              <div
                key={i}
                onClick={() => pick(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 14px",
                  background: bg,
                  cursor: done ? "default" : "pointer",
                  borderLeft:
                    done && i === bugLine
                      ? "3px solid #DC2626"
                      : done
                        ? "3px solid #059669"
                        : "3px solid transparent",
                  transition: "all .12s",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    color: "#6E7681",
                    minWidth: 16,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {i + 1}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    color: "#E6EDF3",
                    lineHeight: 1.8,
                  }}
                  dangerouslySetInnerHTML={{ __html: line }}
                />
              </div>
            );
          })}
        </div>
        {done && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 13px",
              borderRadius: "var(--r-md)",
              background: sel === bugLine ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${sel === bugLine ? "#6EE7B7" : "#FCA5A5"}`,
              fontSize: "0.875rem",
              color: sel === bugLine ? "#065F46" : "#991B1B",
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{
              __html: (sel === bugLine ? "✓ " : "✗ ") + md(explanation),
            }}
          />
        )}
      </div>
    </IWrap>
  );
}

function FillBlank({ prefix, suffix, answers, hint, explanation, onCorrect }) {
  const [val, setVal] = useState("");
  const [status, setStatus] = useState(null);
  function check() {
    const ok = answers.some(
      (a) => val.trim().toLowerCase() === a.toLowerCase(),
    );
    setStatus(ok ? "ok" : "no");
    if (ok) onCorrect?.();
  }
  const bc =
    status === "ok" ? "#059669" : status === "no" ? "#DC2626" : "var(--blue)";
  return (
    <IWrap>
      <IHead
        icon="✏️"
        title="Fill in the blank"
        tag="Complete"
        tagColor="complete"
      />
      <div style={{ padding: 16 }}>
        {hint && (
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--ink-muted)",
              marginBottom: 12,
            }}
          >
            {hint}
          </p>
        )}
        <div
          style={{
            background: "#0D1117",
            borderRadius: "var(--r-md)",
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 4,
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            color: "#E6EDF3",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: prefix }} />
          <input
            value={val}
            onChange={(e) => {
              setVal(e.target.value);
              setStatus(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && check()}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: `2px solid ${bc}`,
              outline: "none",
              color: bc,
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              width: Math.max(val.length * 9, 80) + "px",
              textAlign: "center",
              padding: "0 4px",
            }}
            placeholder="???"
          />
          {suffix && <span dangerouslySetInnerHTML={{ __html: suffix }} />}
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 10,
            alignItems: "center",
          }}
        >
          <button
            onClick={check}
            style={{
              padding: "7px 16px",
              background: "var(--ink)",
              color: "var(--cream)",
              border: "none",
              borderRadius: "var(--r-md)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Check
          </button>
          {status === "ok" && (
            <span style={{ fontSize: 13, color: "#059669", fontWeight: 500 }}>
              ✓ Correct!
            </span>
          )}
          {status === "no" && (
            <span style={{ fontSize: 13, color: "#DC2626" }}>
              Not quite — try again
            </span>
          )}
        </div>
        {status === "ok" && explanation && (
          <div
            style={{
              marginTop: 10,
              padding: "10px 13px",
              background: "#ECFDF5",
              border: "1px solid #6EE7B7",
              borderRadius: "var(--r-md)",
              fontSize: "0.875rem",
              color: "#065F46",
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{ __html: "✓ " + md(explanation) }}
          />
        )}
      </div>
    </IWrap>
  );
}

function TokenBuild({
  slots: slotDefs,
  tokens: pool,
  correct,
  explanation,
  onCorrect,
}) {
  const [slots, setSlots] = useState(Array(slotDefs.length).fill(null));
  const [used, setUsed] = useState([]);
  const [res, setRes] = useState(null);
  const CM = {
    type: { bg: "#DBEAFE", col: "#1E40AF", border: "#93C5FD" },
    name: { bg: "#D1FAE5", col: "#065F46", border: "#6EE7B7" },
    op: { bg: "var(--cream)", col: "var(--ink)", border: "var(--cream-dark)" },
    val: { bg: "#FEF3C7", col: "#92400E", border: "#FCD34D" },
  };
  function place(tok) {
    if (used.includes(tok.id)) return;
    const n = slots.findIndex((s) => s === null);
    if (n === -1) return;
    const ns = [...slots];
    ns[n] = tok;
    setSlots(ns);
    setUsed([...used, tok.id]);
  }
  function clear(i) {
    if (!slots[i]) return;
    setUsed(used.filter((u) => u !== slots[i].id));
    const ns = [...slots];
    ns[i] = null;
    setSlots(ns);
    setRes(null);
  }
  function check() {
    const ok = slots.every((s, i) => s?.val === correct[i]);
    setRes(ok ? "ok" : "no");
    if (ok) onCorrect?.();
  }
  return (
    <IWrap>
      <IHead icon="🧩" title="Build it" tag="Assemble" tagColor="assemble" />
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${slotDefs.length},1fr)`,
            gap: 6,
            marginBottom: 6,
          }}
        >
          {slotDefs.map((l, i) => (
            <div
              key={i}
              style={{
                fontSize: 10,
                color: "var(--ink-muted)",
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              {l}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${slotDefs.length},1fr)`,
            gap: 6,
            marginBottom: 14,
          }}
        >
          {slots.map((s, i) => {
            const c = s ? CM[s.kind] || CM.op : null;
            const ok = res === "ok" && s?.val === correct[i];
            const no = res === "no" && s && s.val !== correct[i];
            return (
              <div
                key={i}
                onClick={() => clear(i)}
                style={{
                  minHeight: 42,
                  border: `1.5px ${s ? "solid" : "dashed"} ${ok ? "#059669" : no ? "#DC2626" : s ? c?.border || "var(--cream-dark)" : "var(--ink-faint)"}`,
                  borderRadius: "var(--r-md)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontFamily: s ? "var(--font-mono)" : "var(--font-sans)",
                  fontWeight: s ? 500 : 400,
                  color: ok
                    ? "#065F46"
                    : no
                      ? "#991B1B"
                      : s
                        ? c?.col || "var(--ink)"
                        : "var(--ink-faint)",
                  background: ok
                    ? "#ECFDF5"
                    : no
                      ? "#FEF2F2"
                      : s
                        ? c?.bg || "var(--cream)"
                        : "var(--cream)",
                  cursor: s ? "pointer" : "default",
                  transition: "all .12s",
                }}
              >
                {s ? (
                  s.label
                ) : (
                  <span style={{ fontSize: 10 }}>click token</span>
                )}
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 7,
            marginBottom: 12,
          }}
        >
          {pool.map((tok) => {
            const c = CM[tok.kind] || CM.op;
            const u = used.includes(tok.id);
            return (
              <span
                key={tok.id}
                onClick={() => place(tok)}
                style={{
                  padding: "5px 11px",
                  borderRadius: "var(--r-md)",
                  border: `1px solid ${c.border}`,
                  background: c.bg,
                  color: c.col,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: u ? "not-allowed" : "pointer",
                  opacity: u ? 0.3 : 1,
                  transition: "all .12s",
                }}
              >
                {tok.label}
              </span>
            );
          })}
        </div>
        <button
          onClick={check}
          style={{
            padding: "7px 16px",
            background: "var(--ink)",
            color: "var(--cream)",
            border: "none",
            borderRadius: "var(--r-md)",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Check
        </button>
        {res === "ok" && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 13px",
              background: "#ECFDF5",
              border: "1px solid #6EE7B7",
              borderRadius: "var(--r-md)",
              fontSize: "0.875rem",
              color: "#065F46",
            }}
            dangerouslySetInnerHTML={{ __html: "✓ " + md(explanation) }}
          />
        )}
        {res === "no" && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 13px",
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: "var(--r-md)",
              fontSize: "0.875rem",
              color: "#991B1B",
            }}
          >
            Not quite — check the order. Click a slot to remove it.
          </div>
        )}
      </div>
    </IWrap>
  );
}

function Predict({ code, question, options, answer, explanation, onCorrect }) {
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  function pick(i) {
    if (done) return;
    setSel(i);
    setDone(true);
    if (i === answer) onCorrect?.();
  }
  return (
    <IWrap>
      <IHead icon="🔮" title="Predict the output" tag="Read" tagColor="read" />
      <div style={{ padding: 16 }}>
        <div
          style={{
            background: "#0D1117",
            borderRadius: "var(--r-md)",
            padding: "14px 16px",
            marginBottom: 14,
          }}
        >
          <pre
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "#E6EDF3",
              lineHeight: 1.9,
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
            dangerouslySetInnerHTML={{ __html: hi(code) }}
          />
        </div>
        <p
          style={{
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "var(--ink)",
            marginBottom: 12,
          }}
        >
          {question}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {options.map((opt, i) => {
            let bg = "#fff",
              border = "var(--cream-dark)",
              col = "var(--ink-soft)";
            if (done && i === answer) {
              bg = "#ECFDF5";
              border = "#059669";
              col = "#065F46";
            } else if (done && i === sel && i !== answer) {
              bg = "#FEF2F2";
              border = "#DC2626";
              col = "#991B1B";
            } else if (!done && i === sel) {
              bg = "var(--blue-light)";
              border = "var(--blue)";
              col = "var(--blue)";
            }
            return (
              <div
                key={i}
                onClick={() => pick(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 13px",
                  border: `1.5px solid ${border}`,
                  borderRadius: "var(--r-md)",
                  cursor: done ? "default" : "pointer",
                  background: bg,
                  color: col,
                  fontSize: "0.875rem",
                  fontFamily: "var(--font-mono)",
                  transition: "all .12s",
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `1.5px solid ${border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 600,
                    flexShrink: 0,
                    background:
                      done && i === answer
                        ? "#059669"
                        : done && i === sel
                          ? "#DC2626"
                          : "transparent",
                    color:
                      done && (i === answer || i === sel) ? "#fff" : "inherit",
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                <span style={{ whiteSpace: "pre" }}>{opt}</span>
              </div>
            );
          })}
        </div>
        {done && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 13px",
              borderRadius: "var(--r-md)",
              background: sel === answer ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${sel === answer ? "#6EE7B7" : "#FCA5A5"}`,
              fontSize: "0.875rem",
              color: sel === answer ? "#065F46" : "#991B1B",
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{
              __html: (sel === answer ? "✓ " : "✗ ") + md(explanation),
            }}
          />
        )}
      </div>
    </IWrap>
  );
}

function Block({ block, onCorrect }) {
  const xp =
    block.type === "spot-bug" || block.type === "token-build" ? 15 : 10;
  const oc = () => onCorrect?.(xp);
  switch (block.type) {
    case "text":
      return <Txt c={block.content} />;
    case "code":
      return (
        <Code
          code={
            block.content ||
            (block.data?.lines
              ? block.data.lines.map((l) => l.v !== undefined ? l.v : l).join("\n")
              : "")
          }
          label={block.label || block.data?.label}
        />
      );
    case "callout":
      return (
        <Callout
          c={
            block.data
              ? (block.data.text ?? block.data.html ?? block.data.content)
              : block.content
          }
          type={block.data ? block.data.variant : block.variant}
        />
      );
    case "quiz":
      return <Quiz {...(block.data ? block.data : block)} onCorrect={oc} />;

    case "spot-bug":
      return <SpotBug {...(block.data ? block.data : block)} onCorrect={oc} />;

    case "fill-blank":
      return (
        <FillBlank {...(block.data ? block.data : block)} onCorrect={oc} />
      );

    case "token-build":
      return (
        <TokenBuild {...(block.data ? block.data : block)} onCorrect={oc} />
      );
    case "hint":
      return <HintBlock block={block} />;
    case "predict":
      return <Predict {...(block.data ? block.data : block)} onCorrect={oc} />;
    default:
      return null;
  }
}

export default Block;
